<template>
  <v-container fluid class="pa-4 pa-sm-6 pa-md-8 mt-8 mb-4 mt-sm-0 mb-sm-0">
    <v-row class="pa-4">
      <v-col cols="12" class="pa-4 pa-sm-6 pa-md-8">
        <v-card elevation="0" style="background-color: transparent; color: white; position: relative;">
          <!-- Botão de Ajuda -->
          <v-btn
            fab
            x-small
            color="primary"
            class="help-button"
            @click="showHelp"
            aria-label="Abrir tutorial de ajuda"
          >
            <v-icon size="16">mdi-help-circle</v-icon>
          </v-btn>
          <v-row class="mb-4 pa-2">
            <!-- Imagem -->
            <v-col cols="12" md="3" class="d-flex justify-center justify-md-start">
              <v-img :src="chapter.title_thumb" aspect-ratio="0.67" max-width="250px"></v-img>
            </v-col>

            <!-- Conteúdo -->
            <v-col cols="12" md="9" class="text-center text-md-left">
              <v-col cols="12" class="d-flex justify-center justify-md-end">
                <v-btn @click="openEditModal" dark class="btn zinnes-primary-btn"
                  :class="{ 'position-absolute': $vuetify.breakpoint.mdAndUp }" style="top: 0; right: 0;">
                  Editar Capítulo
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </v-col>
              <v-card-title class="text-h5 title">Capítulo: {{ chapter.name }}</v-card-title>
              <v-card-subtitle class="subtitle">Páginas: {{ chapter.pagesData.length }}</v-card-subtitle>
              <v-card-text class="description">
                {{ chapter.description }}
              </v-card-text>

              <v-card-text>
                <div class="info-text">
                  <strong class="strong-white">Data de publicação:</strong> {{ chapter.release_date | formataData }}
                </div>
                <div class="info-text">
                  <strong class="strong-white">Última atualização:</strong> {{ chapter.updated_at | formataDataHora }}
                </div>
                <div class="info-text" v-if="chapter.scheduled_at">
                  <strong class="strong-white">Agendamento:</strong> {{ scheduledAtLabel }}
                </div>
                <div class="info-text">
                  <strong class="strong-white">Status:</strong>
                  <v-chip small :color="statusColor">
                    {{ statusLabel }}
                  </v-chip>
                </div>
              </v-card-text>
            </v-col>
          </v-row>

          <!-- Modal de Edição do Capítulo -->
          <v-dialog v-model="isEditModalOpen" max-width="750px">
            <v-card>
              <v-card-title class="headline">Editar Capítulo</v-card-title>
              <v-card-text>
                <v-container>
                  <v-text-field
                    label="Nome"
                    v-model="editableChapter.name"
                    placeholder="Ex: Capítulo 1: O Início"
                    outlined
                    dense
                    :rules="[v => !!v || 'O nome é obrigatório']"
                  ></v-text-field>
                  <v-row>
                    <v-col cols="12" sm="7">
                      <v-textarea
                        label="Descrição"
                        v-model="editableChapter.description"
                        placeholder="Ex: Uma introdução ao mundo da história..."
                        outlined
                        dense
                        rows="3"
                        :rules="[v => !!v || 'A descrição é obrigatória']"
                      ></v-textarea>
                      <v-divider class="my-4"></v-divider>
                      <div v-if="!isPublishedChapter">
                        <div class="d-flex align-center">
                          <h4 class="mb-0">Agendamento</h4>
                          <v-spacer></v-spacer>
                          <v-checkbox
                            v-model="editableChapter.agendar"
                            label="Agendar publicação"
                            dense
                            hide-details
                            class="mt-0"
                            :disabled="scheduleExpired"
                          ></v-checkbox>
                        </div>
                        <v-alert v-if="scheduleExpired" type="warning" dense class="mt-2">
                          Este agendamento já foi executado e não pode ser alterado
                        </v-alert>
                        <v-row v-if="editableChapter.agendar">
                          <v-col cols="12" sm="6">
                            <scheduled-date-field
                              v-model="editableChapter.scheduled_date"
                              label="Data"
                              :disabled="scheduleExpired"
                            ></scheduled-date-field>
                          </v-col>
                          <v-col cols="12" sm="6">
                            <scheduled-time-field-24h
                              label="Hora"
                              v-model="editableChapter.scheduled_time"
                              :disabled="scheduleExpired"
                            ></scheduled-time-field-24h>
                          </v-col>
                        </v-row>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="5">
                      <v-file-input
                        label="Imagem do Capítulo"
                        v-model="editableChapter.thumb"
                        placeholder="Selecione uma imagem (PNG, JPG, WebP ou GIF)"
                        outlined
                        dense
                        accept="image/png, image/jpeg, image/webp, image/gif"
                      ></v-file-input>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="green darken-1" text @click="saveChanges">Salvar</v-btn>
                <v-btn color="red darken-1" text @click="closeEditModal">Cancelar</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-card>
      </v-col>
      <v-col cols="12">
        <v-row align="center" class="px-2">
          <v-col cols="12" sm="auto" class="text-h6 title">Páginas</v-col>

          <!-- Desktop layout -->
          <v-col class="d-none d-sm-flex justify-end">
            <v-btn class="btn mr-2" icon color="white" @click="openPreviewModal">
              <v-icon>mdi-eye</v-icon>
            </v-btn>
            <v-btn @click="openConfirmationDialog" dark class="mr-2 zinnes-primary-btn" :disabled="hasPendingPreviousChapter">
              Publicar
            </v-btn>
            <v-btn dark class="zinnes-primary-btn" @click="openImageUploadModal">
              Adicionar novas páginas
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </v-col>

          <!-- Mobile layout -->
          <v-col class="d-flex d-sm-none flex-column align-center">
            <v-btn block dark class="mb-2 zinnes-primary-btn" @click="openPreviewModal">
              <v-icon>mdi-eye</v-icon>
            </v-btn>
            <v-btn block @click="openConfirmationDialog" dark class="mb-2 zinnes-primary-btn" :disabled="hasPendingPreviousChapter">
              Publicar
            </v-btn>
            <v-btn block dark class="zinnes-primary-btn" @click="openImageUploadModal">
              Adicionar novas páginas
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <div v-if="hasPendingPreviousChapter" class="info-text text-center text-sm-right mt-2">
          Publique o capítulo anterior primeiro
        </div>

        <!-- Grid layout for pages -->
        <v-row ref="drogArea" class="page-grid">
          <v-col v-for="(page) in chapter.pagesData" :key="page.id" :data-id="page.id" cols="12" sm="6" md="4" lg="2"
            class="mb-4 page-col">
            <v-card elevation="2" class="pa-2 page-item drag-handle"
              style="background-color: transparent; color: white;">
              <v-row align="center" no-gutters>
                <v-col cols="12" class="mb-2">
                  <v-img :src="page.image" aspect-ratio="0.67" contain></v-img>
                </v-col>
                <v-col cols="12" class="text-center text-subtitle-1">
                  Página {{ page.page }}
                </v-col>
                <v-col cols="12" class="d-flex justify-center mt-2">
                  <v-btn @click="openEditPageModal(page)" icon class="btn mr-2" color="white" small>
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn @click="deletePage(page.id)" icon class="btn" color="white" small>
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
                <v-icon color="white" class="drag-handle">mdi-menu</v-icon>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Modal de Upload de Páginas -->
    <v-dialog v-model="isModalOpen" persistent max-width="600px">
      <v-card :loading="loading">
        <v-card-title class="headline">Upload de Páginas</v-card-title>
        <v-card-text>
          <v-file-input
            v-model="selectedFiles"
            multiple
            label="Selecione as imagens (PNG, JPG, WebP ou GIF)"
            placeholder="Escolha as imagens das páginas"
            accept="image/png, image/jpeg, image/webp, image/gif"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn color="red" :loading="loading" text @click="closeImageUploadModal">Fechar</v-btn>
          <v-btn color="green" :loading="loading" text @click="uploadImages">Upload</v-btn>
        </v-card-actions>
        <v-card-text>
          <v-progress-linear
            :value="(selectedFiles.reduce((acum, file) => {
              let valor = (file.status ? 1 : 0) + acum;
              return valor;
            }, 0) / selectedFiles.length) * 100"
          ></v-progress-linear>
          <v-list-item v-for="(file, index) in selectedFiles" :key="index">
            <v-list-item-content>
              <v-list-item-title>{{ file.name }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn icon>
                <v-icon :color="file.status ? file.status.color : 'grey lighten-1'">{{ file.status ? file.status.icon :
                  'mdi-information' }}</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Modal de Edição de Página -->
    <v-dialog v-model="isModalOpenEditPageOpen" persistent max-width="600px">
      <v-card>
        <v-card-title class="headline">Editar Página</v-card-title>
        <v-card-text>
          <input
            type="file"
            accept="image/png, image/jpeg, image/webp, image/gif"
            @change="EdithandleFile"
            aria-label="Upload da nova imagem da página"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn color="red" text @click="closeEditPageModal">Cancelar</v-btn>
          <v-btn color="green" text @click="EditUploadImage">Upload</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Visualização de Páginas -->
    <v-dialog v-model="isPreviewModalOpen" fullscreen persistent>
      <v-card>
        <v-card-title>
          <span class="headline">Visualizar Páginas</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closePreviewModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-container fluid>
            <v-row no-gutters>
              <v-col cols="12" v-for="(page, index) in chapter.pagesData" :key="index"
                class="h-screen d-flex justify-center mb-4">
                <v-img :src="page.image" contain class="h-100" style="max-width: 60%;" />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Modal de Confirmação de Publicação -->
    <v-dialog v-model="dialogPublic" max-width="500px">
      <v-card>
        <v-card-title class="headline">Confirmar publicação</v-card-title>
        <v-card-text>
          <p class="text-black">Você está prestes a tornar este capítulo público. Uma vez publicado, não será possível
            torná-lo privado novamente.</p>
        </v-card-text>
        <v-card-actions>
          <v-btn color="red" text @click="dialogPublic = false">Cancelar</v-btn>
          <v-btn color="green" text @click="publishChapter">Confirmar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Tutorial -->
    <v-dialog v-model="helpDialog" max-width="600px">
      <v-card>
        <v-card-title class="headline">Como usar o gerenciador de capítulos</v-card-title>
        <v-card-text>
          <p><strong>Editar Capítulo:</strong> Clique no botão "Editar Capítulo" no canto superior direito para alterar o nome, descrição ou imagem do capítulo.</p>
          <p><strong>Visualizar Páginas:</strong> Clique no ícone de olho (<v-icon>mdi-eye</v-icon>) para abrir uma visualização de todas as páginas do capítulo em tela cheia.</p>
          <p><strong>Publicar Capítulo:</strong> Clique no botão "Publicar" para tornar o capítulo público. Após a publicação, ele não poderá ser tornado privado novamente.</p>
          <p><strong>Adicionar Páginas:</strong> Clique em "Adicionar novas páginas" para fazer upload de novas imagens (PNG, JPG, WebP ou GIF).</p>
          <p><strong>Reorganizar Páginas:</strong> Arraste as páginas usando o ícone de arrastar (<v-icon>mdi-menu</v-icon>) para reordená-las. A nova ordem é salva automaticamente ao soltar a página.</p>
          <p><strong>Editar Página:</strong> Clique no ícone de lápis (<v-icon>mdi-pencil</v-icon>) em uma página para substituir sua imagem.</p>
          <p><strong>Excluir Página:</strong> Clique no ícone de lixeira (<v-icon>mdi-delete</v-icon>) em uma página para removê-la.</p>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" @click="helpDialog = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout" top>
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import Sortable from "sortablejs";
import { getChapterPages, uploadPagesImages, updatePagesChapter, publiChapterPages, getchapters } from "../server/workService.js";
import TitlePageService from "@/server/TitlePageService.js";
import TitleService from "@/server/TitleService.js";
import UtilMixins from "@/mixins/UtilMixins.js";
import { buildScheduledAt, parseScheduledAt, isScheduledAtExpired, formatScheduledAt, formatDateBr } from '@/utils/scheduling';
import { getChapterManagerPath, isRichText } from '@/utils/projectCategory';
import ScheduledDateField from '@/components/ScheduledDateField.vue';
import ScheduledTimeField24h from '@/components/ScheduledTimeField24h.vue';

export default {
  name: "ListPagesCapituloView",
  mixins: [UtilMixins],
  components: { ScheduledDateField, ScheduledTimeField24h },
  props: ["idCapitulo"],
  data() {
    return {
      loading: false,
      isEditModalOpen: false,
      isModalOpenEditPageOpen: false,
      editableChapter: {},
      selectedPage: null,
      selectedFile: null,
      snackbar: {
        show: false,
        message: '',
        color: '',
        timeout: 2500,
      },
      isPreviewModalOpen: false,
      chapter: {
        id: null,
        name: "",
        thumb: "",
        description: "",
        pagesData: [],
        release_date: "",
        last_update: "",
      },
      isModalOpen: false,
      isPreviewOpen: false,
      selectedFiles: [],
      dialogPublic: false,
      service: new TitlePageService(),
      helpDialog: false,
      chapters: [],
    };
  },

  async created() {
    this.init();
  },
  computed: {
    isPublishedChapter() {
      return this.chapter.status === 'publicado' || !!this.chapter.release_date;
    },
    scheduleExpired() {
      return isScheduledAtExpired(this.chapter.scheduled_at);
    },
    statusLabel() {
      const status = this.chapter.status || (!this.chapter.release_date ? 'rascunho' : 'publicado');
      if (status === 'publicado') {
        const publishedAt = formatDateBr(this.chapter.published_at);
        return publishedAt ? `Publicado em ${publishedAt}` : 'Publicado';
      }
      if (status === 'agendado') {
        return `Agendado para ${formatScheduledAt(this.chapter.scheduled_at)}`;
      }
      return 'Não publicado';
    },
    statusColor() {
      const status = this.chapter.status || (!this.chapter.release_date ? 'rascunho' : 'publicado');
      if (status === 'publicado') return 'success';
      if (status === 'agendado') return 'orange';
      return 'error';
    },
    scheduledAtLabel() {
      const value = this.chapter.scheduled_at;
      if (!value) return '';
      const { date, time } = parseScheduledAt(value);
      if (date && time) {
        const [y, m, d] = date.split('-');
        return `${d}/${m}/${y} ${time}`;
      }
      return this.formataData(value, true);
    },
    hasPendingPreviousChapter() {
      if (!this.chapter.id || this.isPublishedChapter) return false;
      const currentId = Number(this.chapter.id);
      return this.chapters.some((c) => {
        if (Number(c.id) >= currentId) return false;
        const status = c.status || (!c.release_date ? 'rascunho' : 'publicado');
        return status === 'rascunho' || status === 'agendado';
      });
    },
  },
  methods: {
    openEditPageModal(page) {
      this.selectedPage = { ...page };
      this.isModalOpenEditPageOpen = true;
    },
    closeEditPageModal() {
      this.isModalOpenEditPageOpen = false;
      this.selectedFile = null;
    },
    openEditModal() {
      const { date, time } = parseScheduledAt(this.chapter.scheduled_at);
      this.editableChapter = {
        ...this.chapter,
        agendar: !!this.chapter.scheduled_at && !this.isPublishedChapter,
        scheduled_date: date,
        scheduled_time: time,
      };
      this.isEditModalOpen = true;
    },
    EdithandleFile(event) {
      const file = event.target.files[0];
      if (file) {
        if (!file.type.startsWith('image/')) {
          this.showSnackbar('Por favor, selecione um arquivo de imagem válido (PNG, JPG, WebP ou GIF).', 'error');
          return;
        }
        if (file.size > 6 * 1024 * 1024) {
          this.showSnackbar('A imagem deve ter no máximo 6MB.', 'error');
          return;
        }
        this.selectedFile = file;
      } else {
        this.showSnackbar('Nenhum arquivo selecionado.', 'error');
      }
    },
    async EditUploadImage() {
      if (!this.selectedFile) {
        this.showSnackbar('Por favor, selecione uma imagem para a página.', 'error');
        return;
      }
      const formData = {
        file: this.selectedFile,
        id: this.selectedPage.id,
        title_id: this.selectedPage.title_id,
        page: this.selectedPage.page,
      };
      try {
        let service = new TitlePageService();
        await service.editImage(formData);
        this.init();
        this.showSnackbar('Página atualizada com sucesso!', 'success');
        this.closeEditPageModal();
      } catch (error) {
        this.showSnackbar(`Erro ao atualizar a página: ${error.message}`, 'error');
      }
    },
    closeEditModal() {
      this.isEditModalOpen = false;
    },
    async saveChanges() {
      if (!this.editableChapter.name) {
        this.showSnackbar('O campo Nome é obrigatório.', 'error');
        return;
      }
      if (!this.editableChapter.description) {
        this.showSnackbar('O campo Descrição é obrigatório.', 'error');
        return;
      }
      if (this.editableChapter.agendar && (!this.editableChapter.scheduled_date || !this.editableChapter.scheduled_time)) {
        this.showSnackbar('Preencha data e hora para o agendamento.', 'error');
        return;
      }
      if (this.editableChapter.agendar) {
        const scheduled = new Date(`${this.editableChapter.scheduled_date}T${this.editableChapter.scheduled_time}:00`);
        if (Number.isNaN(scheduled.getTime())) {
          this.showSnackbar('Data de agendamento inválida.', 'error');
          return;
        }
      }
      const params = {
        id: this.chapter.id,
        project_id: this.chapter.project_id,
        name: this.editableChapter.name,
        description: this.editableChapter.description,
        title_thumb: this.editableChapter.thumb,
        scheduled_at: this.editableChapter.agendar
          ? buildScheduledAt(this.editableChapter.scheduled_date, this.editableChapter.scheduled_time)
          : '',
      };
      try {
        let service = new TitleService();
        await service.edit(params);
        this.init();
        this.$emit("update-chapter", this.editableChapter);
        this.showSnackbar('Capítulo atualizado com sucesso!', 'success');
        this.closeEditModal();
      } catch (error) {
        this.showSnackbar(`Erro ao salvar alterações: ${error.message}`, 'error');
      }
    },
    async init() {
      try {
        const response = await getChapterPages(this.idCapitulo);
        if (response.project && isRichText(response.project)) {
          const path = getChapterManagerPath(response.project);
          if (path) {
            this.showSnackbar('Esta obra usa leitura textual. Redirecionando para o gestor de capítulos.', 'error');
            this.$router.replace(path);
            return;
          }
        }
        this.chapter = {
          ...response,
          pagesData: (response.pages_data || []).sort((a, b) => a.page - b.page),
        };
        if (response.project_id) {
          try {
            const chapters = await getchapters(response.project_id);
            this.chapters = chapters.chapters || [];
          } catch (error) {
            this.chapters = [];
          }
        }
      } catch (error) {
        this.showSnackbar(`Erro ao carregar páginas do capítulo: ${error.message}`, 'error');
      }
    },
    openConfirmationDialog() {
      this.dialogPublic = true;
    },
    async publishChapter() {
      try {
        await publiChapterPages(this.idCapitulo);
        this.dialogPublic = false;
        const response = await getChapterPages(this.idCapitulo);
        this.chapter = {
          ...response,
          pagesData: (response.pages_data || []).sort((a, b) => a.page - b.page),
        };
        this.showSnackbar("Capítulo publicado com sucesso!", 'success');
      } catch (error) {
        this.showSnackbar(`Erro ao publicar capítulo: ${error.message}`, 'error');
      }
    },
    openImageUploadModal() {
      this.isModalOpen = true;
    },
    openPreviewModal() {
      this.isPreviewModalOpen = true;
    },
    closePreviewModal() {
      this.isPreviewModalOpen = false;
    },
    closeImageUploadModal() {
      this.selectedFiles = [];
      this.isModalOpen = false;
    },
    async uploadImages() {
      if (this.selectedFiles.length === 0) {
        this.showSnackbar('Por favor, selecione pelo menos uma imagem.', 'error');
        return;
      }
      this.loading = true;
      try {
        for (const selectedFile of this.selectedFiles) {
          if (!selectedFile.type.startsWith('image/')) {
            this.showSnackbar(`O arquivo ${selectedFile.name} não é uma imagem válida (PNG, JPG, WebP ou GIF).`, 'error');
            selectedFile.status = { color: 'error', icon: "mdi-alert-circle" };
            continue;
          }
          if (selectedFile.size > 6 * 1024 * 1024) {
            this.showSnackbar(`O arquivo ${selectedFile.name} excede o limite de 6MB.`, 'error');
            selectedFile.status = { color: 'error', icon: "mdi-alert-circle" };
            continue;
          }
          let data = {
            title_id: this.idCapitulo,
            images: [selectedFile],
          };
          try {
            await uploadPagesImages(data);
            selectedFile.status = { color: 'success', icon: "mdi-check-circle" };
          } catch (error) {
            selectedFile.status = { color: 'error', icon: "mdi-alert-circle" };
            this.showSnackbar(`Erro ao carregar ${selectedFile.name}: ${error.message}`, 'error');
          }
          this.$forceUpdate();
        }
        const response = await getChapterPages(this.idCapitulo);
        this.chapter = {
          ...response,
          pagesData: (response.pages_data || []).sort((a, b) => a.page - b.page),
        };
        this.showSnackbar("Imagens carregadas com sucesso!", 'success');
        this.closeImageUploadModal();
      } catch (error) {
        this.showSnackbar(`Erro ao carregar imagens: ${error.message}`, 'error');
      }
      this.loading = false;
    },
    async deletePage(id) {
      try {
        let params = { id };
        await this.service.delete(params);
        this.init();
        this.showSnackbar('Página excluída com sucesso!', 'success');
      } catch (error) {
        this.showSnackbar(`Erro ao excluir página: ${error.message}`, 'error');
      }
    },
    async refreshChapter() {
      try {
        const response = await getChapterPages(this.idCapitulo);
        this.chapter = {
          ...response,
          pagesData: (response.pages_data || []).sort((a, b) => a.page - b.page),
        };
      } catch (error) {
        this.showSnackbar(`Erro ao recarregar as páginas do capítulo: ${this.resolveErrorMessage(error)}`, 'error');
      }
    },
    resolveErrorMessage(error) {
      if (!error) return 'Erro desconhecido.';
      if (typeof error === 'string') return error;
      return error.message || 'Erro desconhecido.';
    },
    showSnackbar(message, type) {
      this.snackbar.message = message;
      this.snackbar.color = type === 'success' ? 'green' : 'red';
      this.snackbar.show = true;
    },
    showHelp() {
      this.helpDialog = true;
    }
  },
  mounted() {
    const drogArea = this.$refs.drogArea;
    new Sortable(drogArea, {
      animation: 150,
      handle: '.drag-handle',
      onEnd: async () => {
        const dataNewOrder = {
          title_id: this.idCapitulo,
          pages: Array.from(drogArea.children).map((item, index) => ({
            page_id: parseInt(item.getAttribute("data-id")),
            new_order: index + 1,
          })),
        };
        try {
          await updatePagesChapter(dataNewOrder);
          await this.refreshChapter();
          this.showSnackbar("Nova ordem salva com sucesso!", 'success');
        } catch (error) {
          this.showSnackbar(`Não foi possível salvar a nova ordem das páginas: ${this.resolveErrorMessage(error)}`, 'error');
          await this.refreshChapter();
        }
      },
    });
  }
};
</script>

<style scoped>
.help-button {
  position: absolute;
  top: -20px;
  right: 10px;
  z-index: 10;
}
.chip-not-published {
  background-color: red !important;
  color: white;
}

.chip-published {
  background-color: green !important;
  color: white;
}

.text-black {
  color: #363636 !important;
}

.pa-2 {
  padding: 8px !important;
}

.pa-3 {
  padding: 16px !important;
}

.title {
  font-size: 34px !important;
  font-weight: bold;
  color: #FFFF;
}

.subtitle {
  font-size: 24px;
  font-weight: 600;
  color: #ABABAB;
  margin-top: 12px;
}

.description {
  font-size: 16px;
  font-weight: 500;
  color: #ABABAB;
}

.strong-white {
  font-size: 14px;
  font-weight: bold;
  color: #FFF;
}

.info-text {
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 400;
  color: #ABABAB;
}

.btn {
  height: 40px;
  padding: 8px;
  border-radius: 12px;
  background-color: var(--zinnes-primary);
}

.page-grid {
  display: flex;
  flex-wrap: wrap;
}

.page-item {
  position: relative;
  cursor: move;
}

@media (max-width: 1024px) {
  .v-application .pa-4 {
    padding-left: 45px !important;
  }
}

@media (max-width: 890px) {
  .v-application .pa-4 {
    padding-left: 16px !important;
    padding-bottom: 40px !important;
  }
}

.page-grid {
  display: flex;
  flex-wrap: wrap;
}

.page-col {
  flex: 0 0 12.5%;
  /* 100% / 8 = 12.5% para 8 colunas */
  max-width: 12.5%;
}

.page-item {
  position: relative;
  cursor: move;
}

/* Ajustes para telas menores */
@media (max-width: 1264px) {
  .page-col {
    flex: 0 0 25%;
    /* 4 colunas em telas md */
    max-width: 25%;
  }
}

@media (max-width: 600px) {
  .page-col {
    flex: 0 0 50%;
    /* 2 colunas em telas sm */
    max-width: 50%;
  }
}

/* Estilos existentes */
.chip-not-published {
  background-color: red !important;
  color: white;
}

.chip-published {
  background-color: green !important;
  color: white;
}

.text-black {
  color: #363636 !important;
}

.pa-2 {
  padding: 8px !important;
}

.pa-3 {
  padding: 16px !important;
}

.title {
  font-size: 34px !important;
  font-weight: bold;
  color: #FFFF;
}

.subtitle {
  font-size: 24px;
  font-weight: 600;
  color: #ABABAB;
  margin-top: 12px;
}

.description {
  font-size: 16px;
  font-weight: 500;
  color: #ABABAB;
}

.strong-white {
  font-size: 14px;
  font-weight: bold;
  color: #FFF;
}

.info-text {
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 400;
  color: #ABABAB;
}

.btn {
  height: 40px;
  padding: 8px;
  border-radius: 12px;
  background-color: var(--zinnes-primary);
}
</style>
